import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import { IconCheckbox, IconChevronRight, IconFileDescription, IconSparkles, IconTag } from "@tabler/icons-react"
import { useEffect, useState } from "react"
import type { IProgressiveSummary } from "../../hooks/useStreamingTranscript"
import { formatRelative } from "../../lib/format-date"
import { TopicChips } from "../TopicChips"
import classes from "./InsightsCard.module.css"

interface IProps {
  progressiveSummary: IProgressiveSummary | null
  /** Real number of progressive_summary events received from the backend.
   *  Replaces the previous time-based estimate so a silent backend reads as
   *  silent instead of as fake activity. */
  updateCount: number
  /** ISO timestamp of the most recent progressive_summary event, or null. */
  lastUpdateAt: string | null
  onCollapse: () => void
}

function formatStaleness(
  lastUpdateAt: string | null,
  now: number,
  i18n: ReturnType<typeof useLingui>["i18n"],
): string | null {
  if (!lastUpdateAt) return null
  const last = new Date(lastUpdateAt).getTime()
  if (!Number.isFinite(last)) return null
  const ageMs = Math.max(0, now - last)
  // Below 30s the freshness hint is noise — the summary just updated.
  if (ageMs < 30_000) return null
  return formatRelative(i18n, ageMs, { subMinute: "seconds" })
}

export function InsightsCard({ progressiveSummary, updateCount, lastUpdateAt, onCollapse }: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  // Tick once every 15s so the "last update Xs ago" hint stays honest
  // without spinning a per-second timer.
  const [nowMs, setNowMs] = useState<number>(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 15_000)
    return () => clearInterval(id)
  }, [])

  const topics = progressiveSummary?.topics ?? []
  const summary = progressiveSummary?.currentDiscussion ?? ""
  const keyPoints = progressiveSummary?.keyPoints ?? []
  const staleness = formatStaleness(lastUpdateAt, nowMs, i18n)

  return (
    <div className={classes.card} data-testid="recording-insights">
      <div className={classes.head}>
        <div className={classes.title}>
          <span className={classes.overline}>
            <Trans>Live insights</Trans>
          </span>
          <span className={classes.meta} title={staleness ? i18n._(msg`Last update ${staleness}`) : undefined}>
            <IconSparkles size={12} />
            {updateCount === 0 ? (
              <Trans>waiting…</Trans>
            ) : (
              <>
                <Plural value={updateCount} one="# update" other="# updates" />
                {staleness ? ` · ${staleness}` : null}
              </>
            )}
          </span>
        </div>
        <button
          type="button"
          className={classes.action}
          title={i18n._(msg`Hide insights`)}
          aria-label={i18n._(msg`Hide insights`)}
          onClick={onCollapse}
        >
          <IconChevronRight size={14} />
        </button>
      </div>

      <div className={classes.body}>
        <section className={classes.block}>
          <span className={`${classes.blockTitle} lex-eyebrow`}>
            <IconTag size={11} />
            <Trans>Topics so far</Trans>
          </span>
          {topics.length > 0 ? (
            <TopicChips topics={topics} size="sm" />
          ) : (
            <p className={classes.empty}>
              <Trans>Topics will appear once the conversation gets going.</Trans>
            </p>
          )}
        </section>

        <section className={classes.block}>
          <span className={`${classes.blockTitle} lex-eyebrow`}>
            <IconFileDescription size={11} />
            <Trans>Running summary</Trans>
          </span>
          {summary ? (
            <p className={classes.summary}>{summary}</p>
          ) : (
            <p className={classes.empty}>
              <Trans>Notes appear after a minute or two of talking.</Trans>
            </p>
          )}
        </section>

        <section className={classes.block}>
          <span className={`${classes.blockTitle} lex-eyebrow`}>
            <IconCheckbox size={11} />
            <Trans>Key points detected</Trans>
          </span>
          {keyPoints.length > 0 ? (
            <ul className={classes.actionList}>
              {keyPoints.map((point) => (
                <li key={point} className={classes.actionItem}>
                  <span className={classes.actionBullet} aria-hidden="true" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className={classes.empty}>
              <Trans>Decisions and follow-ups surface here as they're spoken.</Trans>
            </p>
          )}
        </section>
      </div>
    </div>
  )
}

interface IFabProps {
  onShow: () => void
}

export function InsightsFab({ onShow }: IFabProps): React.JSX.Element {
  return (
    <button type="button" className={classes.fab} onClick={onShow} data-testid="recording-insights-fab">
      <IconSparkles size={14} className={classes.fabIcon} />
      <Trans>Show insights</Trans>
    </button>
  )
}
